import { useCallback, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import type { Discipline } from "@/domains/pm/types";
import { groupDisciplinesByCode } from "@/domains/pm/utils/disciplineName";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { cn } from "@/lib/utils";
import { DisciplineBadge } from "./DisciplineBadge";

interface FittedDisciplineBadgesProps {
  disciplines: readonly Discipline[];
  size?: "sm" | "md" | "lg";
  variant?: "solid" | "outline";
  className?: string;
  /** Classes shared by every row of pills. The gap must go here, not on a
   *  parent: the measured row and the drawn rows have to space alike. */
  rowClassName?: string;
  /** Drawn after the last pill (the detail panel's "+") and counted in the fit. */
  trailing?: ReactNode;
}

/**
 * Discipline pills that spell each discipline's name out when every name fits
 * on one line in the space the parent gives, and fall back to the codes
 * (grouped as `DisciplineBadges` groups them) when they don't.
 *
 * Three layers share one box:
 *  - the codes, in flow — they set the box's height and its min-content
 *    width, so a narrow column can still shrink to them;
 *  - a zero-height, clipped copy of the names, measured to decide. Its
 *    `minmax(0, max-content)` track gives it no min-content width but its full
 *    max-content width, so an auto-layout table offers the column room for the
 *    names when it has some to spare, without forcing it;
 *  - the names, drawn over the codes once they fit. Absolutely placed, so the
 *    mode never feeds back into the width it was decided from.
 */
export function FittedDisciplineBadges({
  disciplines,
  size,
  variant,
  className,
  rowClassName,
  trailing,
}: FittedDisciplineBadgesProps) {
  const { language } = useLanguage();
  const containerRef = useRef<HTMLDivElement>(null);
  const namesRef = useRef<HTMLDivElement>(null);
  const trailingRef = useRef<HTMLSpanElement | null>(null);
  const [fits, setFits] = useState(false);

  const measure = useCallback(() => {
    const container = containerRef.current;
    const names = namesRef.current;
    if (!container || !names) return;
    const available = container.getBoundingClientRect().width;
    let needed = names.getBoundingClientRect().width;
    if (trailingRef.current) {
      needed +=
        trailingRef.current.getBoundingClientRect().width +
        (parseFloat(getComputedStyle(names).columnGap) || 0);
    }
    // Zero width means not laid out (jsdom, a collapsed parent): keep the codes.
    // The half pixel absorbs sub-pixel rounding between the two boxes.
    const next = available > 0 && needed <= available + 0.5;
    setFits((prev) => (prev === next ? prev : next));
  }, []);

  const isEmpty = disciplines.length === 0;

  // ResizeObserver delivers before paint, so a resize never flashes the wrong
  // mode. The names row resizes whenever its content does (tags, language);
  // `isEmpty` re-attaches once the rows first mount.
  useLayoutEffect(() => {
    measure();
    const container = containerRef.current;
    const names = namesRef.current;
    if (!container || !names) return;
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    observer.observe(names);
    return () => observer.disconnect();
  }, [measure, isEmpty]);

  if (isEmpty) return null;

  const rowClass = cn("flex items-center gap-1", rowClassName);
  const trailingSlot = trailing ? (
    <span ref={trailingRef} className="inline-flex shrink-0">
      {trailing}
    </span>
  ) : null;
  const names = disciplines.map((d) => (
    <DisciplineBadge key={d.id} discipline={d} size={size} variant={variant} showName />
  ));

  return (
    <div ref={containerRef} className={cn("relative w-full min-w-0", className)}>
      <div className={cn(rowClass, "flex-wrap", fits && "invisible")} aria-hidden={fits}>
        {groupDisciplinesByCode(disciplines, language).map(([first, ...rest]) => (
          <DisciplineBadge
            key={first.id}
            discipline={first}
            additional={rest}
            size={size}
            variant={variant}
          />
        ))}
        {!fits && trailingSlot}
      </div>
      <div
        aria-hidden
        className="invisible grid h-0 grid-cols-[minmax(0,max-content)] overflow-hidden"
      >
        <div ref={namesRef} className={cn(rowClass, "w-max")}>
          {names}
        </div>
      </div>
      {fits && (
        <div className={cn(rowClass, "absolute inset-0")}>
          {names}
          {trailingSlot}
        </div>
      )}
    </div>
  );
}
